import { candidateKeys, useParseJobStatus } from '@/hooks/use-candidates';
import type { ParseConflict, SmartAppendResult } from '@/types/candidates';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ContactConflictModal } from '../components/contact-conflict-modal';

interface CandidateParseJobContextValue {
  activeParseJobId: string | null;
  startParseJob: (parseJobId: string) => void;
}

const CandidateParseJobContext = createContext<CandidateParseJobContextValue | null>(null);

interface CandidateParseJobProviderProps {
  candidateId: string;
  children: React.ReactNode;
}

export function CandidateParseJobProvider({ candidateId, children }: CandidateParseJobProviderProps) {
  const queryClient = useQueryClient();
  const [activeParseJobId, setActiveParseJobId] = useState<string | null>(null);
  // HW-517: queue every contact conflict — backend reports both email and phone,
  // and the modal walks them sequentially.
  const [pendingConflicts, setPendingConflicts] = useState<ParseConflict[]>([]);
  const toastIdRef = useRef<string | number | null>(null);

  const parseStatus = useParseJobStatus(activeParseJobId);

  const startParseJob = useCallback(
    (parseJobId: string) => {
      setActiveParseJobId(parseJobId);
      queryClient.invalidateQueries({ queryKey: candidateKeys.files(candidateId) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
      queryClient.invalidateQueries({ queryKey: [...candidateKeys.all, 'auditLog', candidateId] });
      toastIdRef.current = toast.loading('CV uploaded — parsing in background…', {
        duration: Infinity,
      });
    },
    [candidateId, queryClient],
  );

  useEffect(() => {
    const job = parseStatus.data;
    if (!job || !activeParseJobId) return;

    if (job.status === 'SUCCEEDED') {
      const result = job.result as SmartAppendResult | null;
      const appendedCount = result?.appendedSkillIds.length ?? 0;
      const filled = result?.filledFields ?? [];
      // HW-519: branch on parse outcome (what the parser extracted) — not merge outcome
      // (what we wrote) — so a clean-parse-but-empty-CV gets AC5's warning prompt,
      // and a clean-parse-with-data-already-on-profile keeps the neutral info shape.
      const parsed = result?.parsedFields;
      const hasParsedData =
        !!parsed && (!!parsed.email || !!parsed.phone || parsed.skillIds.length > 0);
      // HW-528: when the parsed email is already owned by another candidate the modal
      // speaks for itself, so the generic "nothing new" toast below is suppressed.
      const emailConflict = result?.conflicts.find((c) => c.field === 'email') ?? null;
      const emailBlocked = Boolean(emailConflict?.blockedByCandidateId);

      if (!hasParsedData) {
        toast.warning('Automatic parsing failed or no data found', {
          description: 'Please fill in the details manually.',
          id: toastIdRef.current ?? undefined,
          duration: 6000,
        });
      } else if (appendedCount > 0 || filled.length > 0) {
        // Story 3 AC4 wants a single-line success toast — sonner's string form keeps
        // "Smart Append: …" on one row, with the "added from CV." qualifier explicit.
        const summary =
          appendedCount > 0
            ? `Smart Append: ${appendedCount} new skill${appendedCount === 1 ? '' : 's'} added from CV.`
            : `Smart Append: ${filled.join(' + ')} auto-filled from CV.`;
        toast.success(summary, {
          id: toastIdRef.current ?? undefined,
          duration: 5000,
        });
      } else if (emailBlocked) {
        // Suppress the generic "nothing new" hint — the blocking modal below
        // already explains why the email was skipped.
        toast.dismiss(toastIdRef.current ?? undefined);
      } else {
        toast.info('CV parsed — nothing new to add', {
          id: toastIdRef.current ?? undefined,
          duration: 4000,
        });
      }
      toastIdRef.current = null;

      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.files(candidateId) });
      queryClient.invalidateQueries({ queryKey: [...candidateKeys.all, 'auditLog', candidateId] });

      // Email first — its 409 may need manual merge — then phone. The blocked-email
      // case (HW-528) rides the same queue; the modal renders its blocked variant.
      const conflicts = result?.conflicts ?? [];
      if (conflicts.length > 0) {
        const queued = [...conflicts].sort((a, b) => {
          if (a.field === b.field) return 0;
          return a.field === 'email' ? -1 : 1;
        });
        setPendingConflicts(queued);
      }

      setActiveParseJobId(null);
    } else if (job.status === 'FAILED') {
      toast.warning('CV parsing failed', {
        id: toastIdRef.current ?? undefined,
        description: job.error ?? 'Fill in the details manually.',
        duration: 6000,
      });
      toastIdRef.current = null;
      queryClient.invalidateQueries({ queryKey: candidateKeys.files(candidateId) });
      setActiveParseJobId(null);
    }
  }, [parseStatus.data, activeParseJobId, queryClient, candidateId]);

  return (
    <CandidateParseJobContext.Provider value={{ activeParseJobId, startParseJob }}>
      {children}
      <ContactConflictModal
        candidateId={candidateId}
        conflict={pendingConflicts[0] ?? null}
        onClose={() => setPendingConflicts((prev) => prev.slice(1))}
      />
    </CandidateParseJobContext.Provider>
  );
}

export function useCandidateParseJob(): CandidateParseJobContextValue {
  const ctx = useContext(CandidateParseJobContext);
  if (!ctx) {
    throw new Error('useCandidateParseJob must be used within CandidateParseJobProvider');
  }
  return ctx;
}